import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader.js';
import { useTenantContext } from '../api/tenant-context.js';
import {
  archiveVariable,
  createVariable,
  listVariables,
  updateVariable,
  type VariableMapping,
  type VariableMappingPatch,
  type VariableSource,
  type VariableStatus,
  type VariableTarget,
  type VariableType,
} from '../api/variables.js';

const SOURCES: VariableSource[] = ['dialog360', 'meta', 'webchat', 'zengine', 'crm-lookup', 'webhook-custom', 'other'];
const TARGETS: VariableTarget[] = ['five9', 'salesforce', 'custom', 'none'];
const TYPES: VariableType[] = ['string', 'number', 'boolean', 'enum', 'array', 'object'];
const STATUSES: VariableStatus[] = ['active', 'error', 'unused'];

const STATUS_TONE: Record<VariableStatus, string> = {
  active: 'badge-ok',
  error: 'badge-err',
  unused: 'badge-warn',
};

export function Variables(): JSX.Element {
  const { tenant } = useTenantContext();
  const tenantId = tenant?.id ?? '';
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<VariableMapping | null>(null);

  const q = useQuery({
    queryKey: ['variables', tenantId],
    queryFn: () => listVariables(tenantId),
    enabled: !!tenantId,
  });
  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['variables', tenantId] });
  };

  const items = q.data ?? [];
  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return items;
    return items.filter((v) =>
      [v.sourceVariable, v.zengineVariable, v.targetVariable, v.source, v.description]
        .filter(Boolean)
        .some((x) => String(x).toLowerCase().includes(s)),
    );
  }, [items, search]);

  const kpis = useMemo(() => {
    const total = items.length;
    const active = items.filter((v) => v.status === 'active').length;
    const error = items.filter((v) => v.status === 'error').length;
    const unused = items.filter((v) => v.status === 'unused').length;
    return { total, active, error, unused };
  }, [items]);

  return (
    <>
      <PageHeader
        title="Variables"
        subtitle="Mapeo de variables entre canales de origen, ZEngine y sistemas destino (Five9, CRM, etc)."
      />

      <div className="grid grid-cols-4 gap-3 mb-5">
        <Kpi label="Total mapeos" value={kpis.total} />
        <Kpi label="Activos" value={kpis.active} tone="ok" />
        <Kpi label="Con errores" value={kpis.error} tone="err" />
        <Kpi label="Sin usar" value={kpis.unused} tone="warn" />
      </div>

      <div className="flex items-center gap-3 mb-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar variable…"
          className="input flex-1 max-w-md"
        />
        <button type="button" onClick={() => setCreating((v) => !v)} className="btn-primary">
          {creating ? 'Cancelar' : '+ Nuevo mapeo'}
        </button>
      </div>

      {creating && (
        <VariableForm
          tenantId={tenantId}
          mode="create"
          onSuccess={() => {
            setCreating(false);
            refresh();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {q.isLoading && <div className="panel text-ink-3 text-sm">Cargando…</div>}
      {!q.isLoading && filtered.length === 0 && !creating && (
        <div className="panel text-ink-3 text-sm">
          {search ? 'Sin resultados para tu búsqueda.' : 'Sin mapeos. Creá uno para conectar variables entre canales.'}
        </div>
      )}

      {filtered.length > 0 && (
        <div className="panel" style={{ padding: 0 }}>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] text-ink-4">
                <Th>Variable origen</Th>
                <Th>Fuente</Th>
                <Th>Variable ZEngine</Th>
                <Th>Target</Th>
                <Th>Variable destino</Th>
                <Th>Tipo</Th>
                <Th>Estado</Th>
                <Th className="text-right">Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) =>
                editing === v.id ? (
                  <tr key={v.id} className="border-t border-border">
                    <td colSpan={8} className="py-3">
                      <VariableForm
                        tenantId={tenantId}
                        mode="edit"
                        initial={v}
                        onSuccess={() => {
                          setEditing(null);
                          refresh();
                        }}
                        onCancel={() => setEditing(null)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={v.id} className="border-t border-border">
                    <td className="px-3 py-2.5 text-ink-1 mono">{v.sourceVariable}</td>
                    <td className="px-3 py-2.5 text-ink-2">{v.source}</td>
                    <td className="px-3 py-2.5 text-ink-1 mono">{v.zengineVariable}</td>
                    <td className="px-3 py-2.5 text-ink-2">{v.targetSystem}</td>
                    <td className="px-3 py-2.5 mono text-ink-2">{v.targetVariable ?? '—'}</td>
                    <td className="px-3 py-2.5"><span className="badge badge-neutral">{v.type}</span></td>
                    <td className="px-3 py-2.5">
                      <span className={`badge ${STATUS_TONE[v.status]}`}>{v.status}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex gap-1">
                        <button type="button" onClick={() => setEditing(v.id)} className="btn-mini">Editar</button>
                        <button type="button" onClick={() => setConfirmArchive(v)} className="btn-mini btn-mini-danger">Archivar</button>
                      </div>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}

      {confirmArchive && (
        <ConfirmModal
          title="¿Archivar mapeo?"
          desc={`${confirmArchive.sourceVariable} → ${confirmArchive.zengineVariable}`}
          confirmLabel="Archivar"
          onConfirm={async () => {
            await archiveVariable(tenantId, confirmArchive.id);
            setConfirmArchive(null);
            refresh();
          }}
          onCancel={() => setConfirmArchive(null)}
        />
      )}
    </>
  );
}

function VariableForm({
  tenantId, mode, initial, onSuccess, onCancel,
}: {
  tenantId: string;
  mode: 'create' | 'edit';
  initial?: VariableMapping;
  onSuccess: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [source, setSource] = useState<VariableSource>(initial?.source ?? 'dialog360');
  const [sourceVariable, setSourceVariable] = useState(initial?.sourceVariable ?? '');
  const [zengineVariable, setZengineVariable] = useState(initial?.zengineVariable ?? '');
  const [targetSystem, setTargetSystem] = useState<VariableTarget>(initial?.targetSystem ?? 'none');
  const [targetVariable, setTargetVariable] = useState(initial?.targetVariable ?? '');
  const [type, setType] = useState<VariableType>(initial?.type ?? 'string');
  const [status, setStatus] = useState<VariableStatus>(initial?.status ?? 'active');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      if (!sourceVariable.trim()) throw new Error('sourceVariable es obligatorio');
      if (!zengineVariable.trim()) throw new Error('zengineVariable es obligatorio');
      if (targetSystem !== 'none' && !targetVariable.trim()) {
        throw new Error('targetVariable obligatorio cuando target ≠ none');
      }
      const payload = {
        source,
        sourceVariable: sourceVariable.trim(),
        zengineVariable: zengineVariable.trim(),
        targetSystem,
        ...(targetVariable.trim() ? { targetVariable: targetVariable.trim() } : {}),
        type,
        status,
        ...(description.trim() ? { description: description.trim() } : {}),
      };
      if (mode === 'create') return createVariable(tenantId, payload);
      if (initial) {
        const patch: VariableMappingPatch = payload;
        return updateVariable(tenantId, initial.id, patch, initial.version);
      }
      throw new Error('invalid');
    },
    onSuccess: () => onSuccess(),
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'error'),
  });

  return (
    <div className="rounded border border-border bg-bg-2 p-4 space-y-3 mx-3 my-2">
      <div className="text-[13px] font-semibold">
        {mode === 'create' ? 'Nuevo mapeo de variable' : `Editar ${initial?.sourceVariable}`}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Fuente">
          <select value={source} onChange={(e) => setSource(e.target.value as VariableSource)} className="input">
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Tipo">
          <select value={type} onChange={(e) => setType(e.target.value as VariableType)} className="input">
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Variable origen" helper="Ej: from.wa_id, profile.name. Formato dot.path.">
        <input type="text" value={sourceVariable} onChange={(e) => setSourceVariable(e.target.value)} className="input mono" maxLength={120} />
      </Field>
      <Field label="Variable ZEngine" helper="Ej: contact.phone. Cómo se renombra en ZE.">
        <input type="text" value={zengineVariable} onChange={(e) => setZengineVariable(e.target.value)} className="input mono" maxLength={120} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Sistema destino">
          <select value={targetSystem} onChange={(e) => setTargetSystem(e.target.value as VariableTarget)} className="input">
            {TARGETS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Variable destino" helper={targetSystem === 'none' ? 'No aplica.' : 'Cómo se envía al sistema destino.'}>
          <input
            type="text"
            value={targetVariable}
            onChange={(e) => setTargetVariable(e.target.value)}
            className="input mono"
            disabled={targetSystem === 'none'}
            maxLength={120}
          />
        </Field>
      </div>
      <Field label="Estado">
        <div className="flex gap-2">
          {STATUSES.map((s) => (
            <label
              key={s}
              className={`flex items-center gap-2 rounded border px-3 py-1 text-[12px] cursor-pointer ${
                status === s ? 'border-brand bg-brand-soft text-ink-1' : 'border-border text-ink-3 hover:border-border-2'
              }`}
            >
              <input type="radio" name="status" checked={status === s} onChange={() => setStatus(s)} />
              {s}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Descripción (opcional)">
        <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} className="input" />
      </Field>
      {err && <p className="text-[12px] text-err">{err}</p>}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
        <button type="button" onClick={() => mut.mutate()} disabled={mut.isPending} className="btn-primary">
          {mut.isPending ? '…' : mode === 'create' ? 'Crear mapeo' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone?: 'ok' | 'warn' | 'err' }): JSX.Element {
  const color = tone === 'ok' ? 'text-ok' : tone === 'err' ? 'text-err' : tone === 'warn' ? 'text-warn' : 'text-ink-1';
  return (
    <div className="panel" style={{ padding: '14px 18px' }}>
      <div className="text-[11px] uppercase tracking-wide text-ink-4">{label}</div>
      <div className={`mt-1 text-[22px] font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return (
    <th className={`px-3 py-2.5 font-medium ${className ?? ''}`} style={{ background: 'var(--bg-2)' }}>
      {children}
    </th>
  );
}

function Field({ label, helper, children }: { label: string; helper?: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="block mb-1 text-[12px] text-ink-3">{label}</span>
      {children}
      {helper && <span className="block mt-1 text-[10.5px] text-ink-4">{helper}</span>}
    </label>
  );
}

function ConfirmModal({
  title, desc, confirmLabel, onConfirm, onCancel,
}: {
  title: string; desc: string; confirmLabel: string;
  onConfirm: () => void | Promise<void>; onCancel: () => void;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-lg border border-border-2 bg-surface p-5">
        <div className="text-[15px] font-semibold mb-2">{title}</div>
        <p className="text-[13px] text-ink-3 mb-4">{desc}</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
          <button type="button" onClick={() => void onConfirm()} className="btn-primary" style={{ background: 'var(--err)' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
